<h1 mat-dialog-title>Add to Risk Register</h1>

<div mat-dialog-content>
    <div class="p-t-20 field-column risk-dialog">

        <!--
          THE VERBATIM QUOTE (D8), READ-ONLY.

          First thing on the dialog, before any editable field. §1: "No editing of an item's
          AI-extracted quote. The quote is evidence; it is verbatim or it is gone." The doctor is
          about to put this on their own letterhead, so they read the sentence they actually said
          before anything else.
        -->
        <div class="risk-dialog-quote" *ngIf="quote">
            <div class="risk-dialog-quote-label">
                <mat-icon>format_quote</mat-icon>
                <span>What you said (recorded verbatim, and not editable)</span>
            </div>
            <blockquote>{{ quote }}</blockquote>
        </div>

        <!-- Only for an AI suggestion that arrived WITHOUT its quote — a real loss of the D8
             evidence control. A hand-typed item never had one and never should, so
             `!isManualEntry` keeps this quiet there; warning about the normal case would
             train the user to ignore the warning that means something. -->
        <div class="risk-dialog-note risk-dialog-warn" *ngIf="!quote && !isManualEntry">
            <mat-icon>report_problem</mat-icon>
            <span>
                No verbatim quote was captured for this issue. It will be added to the register with
                no supporting quotation.
            </span>
        </div>

        <mat-form-field appearance="outline">
            <mat-label>Date of issue</mat-label>
            <input matInput [matDatepicker]="riskDatePicker" [(ngModel)]="model.dateOfIssue"
                placeholder="DD/MM/YYYY" [max]="todayDate">
            <mat-datepicker-toggle matSuffix [for]="riskDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #riskDatePicker></mat-datepicker>
            <mat-hint>The day the issue happened at the facility, not today.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline">
            <mat-label>Issue</mat-label>
            <textarea matInput cdkTextareaAutosize="true" rows="2" [(ngModel)]="model.issue"></textarea>
        </mat-form-field>

        <!--
          THE FACILITY (§4 / D6). Which hospital this row will name, and which branch of the
          resolution chain produced it. `In-rooms / OPD` is labelled as the legitimate outcome it
          is under D2 — it is NOT an error state and is never presented as one.

          MOVED ABOVE CATEGORY to match the routed page, where the facility block sits between Date
          of issue and Category. `.risk-dialog-facility` carries a top margin there for the same
          reason it does here — parity of rhythm; the `mat-hint` under Date of issue cannot
          actually collide in this dialog, because the Issue field sits between the two.
        -->

        <!--
          THE FACILITY PICKER — pre-filled from the preview above, and EDITABLE.

          §4 resolves where an issue happened from admission and appointment data; it does not
          always get it right, and the doctor was standing there. So the resolved answer is offered
          as a DEFAULT and the doctor's change is what gets stored. Leaving it alone posts nothing
          and the server's chain fills it in instead — which is still the better answer in the one
          case the browser cannot cover, a hospital deactivated since the admission was recorded.

          Hospital → ward is the dependent select from `admit-details.component.html:28-37`,
          reading the wards already nested on each hospital.
        -->
        <!-- No heading and no restated summary: the select below already says where this is
             filed, so a prose line above it saying the same thing was chrome. The CONDITIONAL
             lines further down stay — each says something the control cannot. -->
        <div class="risk-dialog-facility-picker">

            <!--
              ONE SELECT — the mode and the hospital are the same question.

              In-rooms / OPD is the FIRST OPTION rather than the off position of a toggle: D2/D6
              make it a first-class outcome, named as a place, and a list of places is where a place
              belongs. Two controls could disagree (facility mode, no hospital); one cannot, so
              `needsHospitalSelection`, its banner and its `canSave` term are gone.
            -->
            <mat-form-field appearance="outline">
                <mat-label>Where this happened</mat-label>
                <mat-select [(ngModel)]="facilitySelection" (selectionChange)="onFacilityChange()">
                    <mat-option [value]="roomsValue">In-rooms / OPD</mat-option>
                    <mat-option *ngFor="let hospital of hospitals" [value]="hospital.id">
                        {{ hospital.name }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <!-- Only once a hospital is chosen, and only when that hospital has wards. A
                 procedure is booked to a hospital and not to a bed, so no ward is a normal
                 answer and "No ward" is an explicit option rather than an empty select. -->
            <mat-form-field appearance="outline" *ngIf="selectedHospitalId && wards.length">
                <mat-label>Ward (optional)</mat-label>
                <mat-select [(ngModel)]="selectedWardId">
                    <mat-option [value]="null">No ward / not specified</mat-option>
                    <mat-option *ngFor="let ward of wards" [value]="ward.id">{{ ward.name }}</mat-option>
                </mat-select>
            </mat-form-field>

            <!-- Still gated on `facilityMode === 'facility'`, which now means "the sentinel is not
                 the selection" — i.e. the doctor is looking at a facility answer and the list
                 behind it is empty. Saying it when they have deliberately chosen In-rooms / OPD
                 would be noise. -->
            <p class="risk-dialog-facility-hint" *ngIf="!hospitals.length && facilityMode === 'facility'">
                No hospitals are loaded for this practice, so In-rooms / OPD is the only thing this
                list can offer. Choose it, or leave this as it is and the server will resolve the
                facility.
            </p>

            <!--
              "SAY SO PLAINLY." The doctor has moved this away from what the chain worked out, and
              the row will record `Chosen by you on the add form` rather than a §4 branch. Saying
              it here is what makes that label legible afterwards.
            -->
            <div class="risk-dialog-note risk-dialog-override" *ngIf="isFacilityDirty">
                <mat-icon>edit_location_alt</mat-icon>
                <span>
                    You have changed this from <strong>{{ facilityLine || 'your rooms' }}</strong>
                    ({{ resolutionLabel }}). It will be filed as
                    <strong>{{ chosenFacilityLine }}</strong> and recorded as your own choice, not
                    as a location the system worked out.
                </span>
            </div>
        </div>

        <!-- D7 — a CLOSED taxonomy. No free text: "17 consumable stock-outs at Hospital X this
             month" is a conversation with a hospital; 60 loose sentences is a shrug. -->
        <mat-form-field appearance="outline">
            <mat-label>Category</mat-label>
            <mat-select [(ngModel)]="model.category">
                <mat-option *ngFor="let category of categories" [value]="category">
                    {{ categoryLabel(category) }}
                </mat-option>
            </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
            <mat-label>Severity</mat-label>
            <mat-select [(ngModel)]="model.severity">
                <mat-option *ngFor="let severity of severities" [value]="severity">
                    {{ severityLabel(severity) }}
                </mat-option>
            </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
            <mat-label>Notes (optional)</mat-label>
            <textarea matInput cdkTextareaAutosize="true" rows="4" [(ngModel)]="model.notes"></textarea>
        </mat-form-field>

        <!-- §28 / P5 / D15 — PHOTOGRAPHS, ON THE SUGGESTION DIALOG TOO.

             D15 reversed the original "keep the dialog fast" recommendation, and the reasoning is
             the user's scenario verbatim: the doctor photographed the instrument in theatre and is
             now doing their notes, and THE MOMENT THE SUGGESTION APPEARS is exactly when they
             remember the photo exists. Picking it from the gallery is two taps. Making them save
             the item, find it in the register and re-open it is how the photo never gets attached.

             `image/*` only — the server refuses anything else and the gallery would filter a PDF
             out of view anyway (P4). -->
        <div class="risk-dialog-media">
            <app-file-upload [fileObjects]="[]" [allowMultiple]="true" [allowedFileTypes]="'image/*, application/pdf'"
                title="Photographs (optional)"
                hintMessage="Attach a photo of the equipment, consumable or facility issue."
                (fileUpload)="onRiskMediaUpload($event)" (fileDelete)="onRiskMediaDelete($event)"
                (filesEdited)="onRiskMediaEdited($event)" [cameraEnabled]="true"
                [enableEditing]="true">
            </app-file-upload>
        </div>

        <div class="risk-dialog-note" *ngIf="reason">
            <mat-icon>info</mat-icon>
            <span>{{ reason }}</span>
        </div>

    </div>
</div>

<div mat-dialog-actions class="dialog-actions">
    <button mat-button type="button" (click)="cancel()">Cancel</button>
    <button mat-raised-button type="button" color="primary" [disabled]="!canSave" (click)="save()">
        Add to Register
    </button>
</div>
